import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin } from 'lucide-react';

// Deterministic pastel background from the name so the avatar isn't jarring
function avatarBg(name) {
  const seed = (name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const hues = ['#0ea5e9', '#2563eb', '#7c3aed', '#0f766e', '#b45309', '#be123c'];
  return hues[seed % hues.length];
}

function initials(name) {
  return (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0].toUpperCase())
    .join('');
}

export default function AuthorCard({ author }) {
  if (!author || !author.name) return null;
  const hasLinkedIn = typeof author.linkedin === 'string' && author.linkedin.startsWith('http');

  return (
    <section
      className="mt-16 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 flex flex-col sm:flex-row gap-6 items-start sm:items-center"
      data-testid="guide-author-card"
    >
      {author.photo ? (
        <img
          src={author.photo}
          alt={author.name}
          className="w-16 h-16 rounded-full object-cover flex-shrink-0"
          data-testid="author-photo"
        />
      ) : (
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center text-white text-lg font-semibold flex-shrink-0"
          style={{ background: avatarBg(author.name) }}
          data-testid="author-initials"
        >
          {initials(author.name)}
        </div>
      )}

      <div className="flex-1 min-w-0">
        <p className="text-xs uppercase tracking-wider text-slate-400">Written by</p>
        {author.slug ? (
          <Link
            to={`/authors/${author.slug}`}
            data-testid="author-card-name"
            className="mt-1 inline-block text-lg font-semibold text-slate-900 hover:text-blue-600 transition-colors"
          >
            {author.name}
          </Link>
        ) : (
          <p className="mt-1 text-lg font-semibold text-slate-900" data-testid="author-card-name">
            {author.name}
          </p>
        )}
        {author.description && (
          <p className="mt-1 text-sm text-slate-600 leading-relaxed">{author.description}</p>
        )}
        {hasLinkedIn && (
          <a
            href={author.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="author-linkedin"
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            <Linkedin className="w-4 h-4" />
            Connect on LinkedIn
          </a>
        )}
      </div>
    </section>
  );
}
